import { useSelector } from "@tanstack/react-store";
import { useMemo } from "react";

import { Switch } from "@aircall/ds";

import { useEvaluationUpdateForm } from "@/hooks/useEvaluationUpdateForm/useEvaluationUpdateForm";
import {
  EvaluationField,
  EvaluationTextarea,
  PickInputChoices,
} from "../../../EvaluationField";
import {
  buildBinaryRatingScale,
  buildMultipleRatingScale,
  getDescription,
  getHighestPoints,
  getRating,
  hasNotApplicableAnswer,
} from "../../EvaluationUpdateForm.utility";

import {
  BinaryAnswerValue,
  RatingAnswerValue,
} from "@/graphql/generated/graphql";

import { ExcludeNotApplicable } from "@/components/EvaluationResult/EvaluationResult.decl";
import {
  EvaluationFormBinaryQuestionProps,
  EvaluationFormRatingQuestionProps,
} from ".";

export function EvaluationUpdateFormRatingQuestion({
  possibleAnswers,
  order,
  text,
  categoryIndex,
  questionIndex,
}: EvaluationFormRatingQuestionProps) {
  const { form } = useEvaluationUpdateForm();

  const fieldQuestion = useSelector(
    form.store,
    (state) => state.values.categories[categoryIndex].questions[questionIndex]
  );

  // Rating scale for the EvaluationField component.
  const fieldRatingScale = useMemo(
    () => buildMultipleRatingScale(possibleAnswers),
    [possibleAnswers]
  );

  const maximumPoints = useMemo(
    () => possibleAnswers.reduce(getHighestPoints, 0),
    [possibleAnswers]
  );

  return (
    <EvaluationField<ExcludeNotApplicable<RatingAnswerValue>>
      mode="multiple"
      rating={
        getRating(fieldQuestion) as ExcludeNotApplicable<RatingAnswerValue>
      }
      notApplicable={hasNotApplicableAnswer(fieldQuestion)}
      // We want to always show the not applicable toggle for rating questions
      possiblyNotApplicable
      description={getDescription(fieldQuestion)}
      comment={fieldQuestion.feedback ?? ""}
      rank={order}
      question={text}
      maximumPoints={maximumPoints}
      ratingScale={fieldRatingScale as Record<RatingAnswerValue, number>}
      renderNotApplicableToggle={
        <form.Field
          name={`categories[${categoryIndex}].questions[${questionIndex}].ratingAnswer`}
        >
          {(field) => (
            <Switch
              data-test={`evaluation-na-toggle-c${categoryIndex}-q${questionIndex}`}
              checked={field.state.value === "NOT_APPLICABLE"}
              onCheckedChange={(bool) => {
                field.handleChange(
                  bool ? RatingAnswerValue.NOT_APPLICABLE : null
                );
              }}
            />
          )}
        </form.Field>
      }
      renderFeedback={
        <form.Field
          name={`categories[${categoryIndex}].questions[${questionIndex}].feedback`}
        >
          {/* TODO: Replace  "undefined" when we have the right data */}
          {(field) => (
            <EvaluationTextarea
              dataTest={`evaluation-feedback-c${categoryIndex}-q${questionIndex}`}
              comment={field.state.value ?? ""}
              onChangeComment={(event) => {
                field.handleChange(event.target.value);
              }}
              hasAiAnswer={undefined}
              loading={undefined}
            />
          )}
        </form.Field>
      }
      renderInputChoices={
        <form.Field
          name={`categories[${categoryIndex}].questions[${questionIndex}].ratingAnswer`}
        >
          {(field) => (
            <PickInputChoices
              mode="multiple"
              // TODO: Check if the assert makes sense here.
              value={field.state.value as RatingAnswerValue}
              onChange={(value) => {
                field.handleChange(value);
              }}
              disabled={field.state.value === "NOT_APPLICABLE"}
              ratingScale={
                fieldRatingScale as Record<RatingAnswerValue, number>
              }
            />
          )}
        </form.Field>
      }
    />
  );
}

export function EvaluationUpdateFormBinaryQuestion({
  possibleAnswers,
  order,
  text,
  categoryIndex,
  questionIndex,
}: EvaluationFormBinaryQuestionProps) {
  const { form } = useEvaluationUpdateForm();

  const fieldQuestion = useSelector(
    form.store,
    (state) => state.values.categories[categoryIndex].questions[questionIndex]
  );

  // Rating scale for the EvaluationField component.
  const fieldRatingScale = useMemo(
    () => buildBinaryRatingScale(possibleAnswers),
    [possibleAnswers]
  );

  const maximumPoints = useMemo(
    () => possibleAnswers.reduce(getHighestPoints, 0),
    [possibleAnswers]
  );

  return (
    <EvaluationField<ExcludeNotApplicable<BinaryAnswerValue>>
      mode="binary"
      rating={
        getRating(fieldQuestion) as ExcludeNotApplicable<BinaryAnswerValue>
      }
      notApplicable={hasNotApplicableAnswer(fieldQuestion)}
      // We want to always show the not applicable toggle for binary questions
      possiblyNotApplicable
      description={getDescription(fieldQuestion)}
      comment={fieldQuestion.feedback ?? ""}
      rank={order}
      question={text}
      maximumPoints={maximumPoints}
      ratingScale={fieldRatingScale as Record<BinaryAnswerValue, number>}
      renderNotApplicableToggle={
        <form.Field
          name={`categories[${categoryIndex}].questions[${questionIndex}].binaryAnswer`}
        >
          {(field) => (
            <Switch
              data-test={`evaluation-na-toggle-c${categoryIndex}-q${questionIndex}`}
              checked={field.state.value === "NOT_APPLICABLE"}
              onCheckedChange={(bool) => {
                field.handleChange(
                  bool ? BinaryAnswerValue.NOT_APPLICABLE : null
                );
              }}
            />
          )}
        </form.Field>
      }
      renderFeedback={
        <form.Field
          name={`categories[${categoryIndex}].questions[${questionIndex}].feedback`}
        >
          {/* TODO: Replace  "undefined" when we have the right data */}
          {(field) => (
            <EvaluationTextarea
              dataTest={`evaluation-feedback-c${categoryIndex}-q${questionIndex}`}
              comment={field.state.value ?? ""}
              onChangeComment={(event) => {
                field.handleChange(event.target.value);
              }}
              hasAiAnswer={undefined}
              loading={undefined}
            />
          )}
        </form.Field>
      }
      renderInputChoices={
        <form.Field
          name={`categories[${categoryIndex}].questions[${questionIndex}].binaryAnswer`}
        >
          {(field) => (
            <PickInputChoices
              mode="binary"
              // TODO: Check if the assert makes sense here.
              value={field.state.value as BinaryAnswerValue}
              onChange={(value) => {
                field.handleChange(value);
              }}
              disabled={field.state.value === "NOT_APPLICABLE"}
              ratingScale={
                fieldRatingScale as Record<BinaryAnswerValue, number>
              }
            />
          )}
        </form.Field>
      }
    />
  );
}
